import api from './api';

export interface LibroMayorSearchParams {
  fechaDesde: string;
  fechaHasta: string;
  nroCuenta: number;
  pageNumber: number;
  pageSize: number;
}

export interface LibroMayorExportParams {
  fechaDesde: string;
  fechaHasta: string;
  nroCuenta: number;
}

export interface LibroMayorLinea {
  fechaAsiento: string | null;
  nroAsiento: number;
  comprobante: string;
  detalle: string | null;
  debe: number;
  haber: number;
  saldo: number;
  nroId: number;
}

export interface LibroMayorResult {
  nroCuenta: number;
  nomCuenta: string | null;
  saldoInicial: number;
  totalMovimientos: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  lineas: LibroMayorLinea[];
}

/** Listado completo para Excel (GET export-data). */
export interface LibroMayorCompleto {
  nroCuenta: number;
  nomCuenta: string | null;
  fechaDesde: string;
  fechaHasta: string;
  saldoInicial: number;
  totalMovimientos: number;
  lineas: LibroMayorLinea[];
}

function unwrapMessage(error: unknown): string {
  const ax = error as { response?: { data?: { message?: string } } };
  const m = ax.response?.data?.message;
  return typeof m === 'string' ? m : 'Error en la operación';
}

const libroMayorService = {
  getLibroMayor: async (params: LibroMayorSearchParams): Promise<LibroMayorResult> => {
    const response = await api.get<LibroMayorResult>('/api/LibroMayor', {
      params: {
        fechaDesde: params.fechaDesde,
        fechaHasta: params.fechaHasta,
        nroCuenta: params.nroCuenta,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize
      }
    });
    return response.data;
  },

  getExportData: async (params: LibroMayorExportParams): Promise<LibroMayorCompleto> => {
    const response = await api.get<LibroMayorCompleto>('/api/LibroMayor/export-data', {
      params: {
        fechaDesde: params.fechaDesde,
        fechaHasta: params.fechaHasta,
        nroCuenta: params.nroCuenta
      }
    });
    return response.data;
  },

  /** PDF del libro mayor (mismos filtros que la consulta). */
  getPreviewPdfBlob: async (params: LibroMayorExportParams): Promise<Blob> => {
    try {
      const response = await api.get<Blob>('/api/LibroMayor/preview-pdf', {
        params: {
          fechaDesde: params.fechaDesde,
          fechaHasta: params.fechaHasta,
          nroCuenta: params.nroCuenta
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (err: unknown) {
      const ax = err as { response?: { data?: Blob } };
      const blobErr = ax.response?.data;
      if (blobErr instanceof Blob && blobErr.size < 8192) {
        const t = await blobErr.text();
        try {
          const j = JSON.parse(t) as { message?: string };
          if (typeof j.message === 'string' && j.message.length > 0) {
            throw new Error(j.message);
          }
        } catch (e: unknown) {
          if (e instanceof SyntaxError) {
            /* no es JSON */
          } else if (e instanceof Error) {
            throw e;
          }
        }
      }
      throw new Error(unwrapMessage(err));
    }
  },

  parseError: unwrapMessage
};

export default libroMayorService;
